import React from 'react';
import { ReactComponent as ProfileIconSvg } from '../../../../assets/icons/profile.svg';
import { ReactComponent as HubIconSvg } from '../../../../assets/icons/location.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import BrandImage from '@src/assets/img/brand.png';
import { HeaderProps } from './types';
import { useNavigate } from 'react-router-dom';
import { HEADER_HEIGHT } from '@src/layouts/root-layout/config/const';
import { routesConfig } from '@src/core/routes';
import Notifications from './notifications';
import { useClickMenu } from './useClickMenu';
import { headerTriggerSx } from './trigger-sx';
import { useGetUserDataQuery } from '@src/core/features';
import { formatProfileNameForHeader } from '@src/features/profile/utils/profile-name-rules';
import { ReactComponent as ProfileCircleIcon } from 'src/assets/icons/profile-circle.svg';
import { useConfirm } from '@src/core/controllers';
import { ACCESS_TOKEN_KEY } from '@src/core/config';
import { Popper } from '@mui/material';
import { alpha, type SxProps, Theme } from '@mui/material/styles';

const Header: React.FC<HeaderProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { data: userData } = useGetUserDataQuery();
  const profileMenu = useClickMenu();
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [accountTipOpen, setAccountTipOpen] = React.useState(false);

  React.useEffect(() => {
    if (!profileMenu.open) return;

    const isInside = (target: Node) =>
      [profileMenu.anchorEl, menuRef.current].some((node) => node?.contains(target));

    const onPointerDown = (event: MouseEvent) => {
      if (isInside(event.target as Node)) return;
      profileMenu.close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') profileMenu.close();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [profileMenu.open, profileMenu.anchorEl, profileMenu.close]);

  const goTo = (path: string) => {
    profileMenu.close();
    navigate(path);
  };

  const logout = async () => {
    if (await confirm('Are you sure you want to logout of your account?', 'Logout')) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      window.location.reload();
    }
  };

  const menuRowSx: SxProps<Theme> = {
    px: 2,
    py: 1,
    cursor: 'pointer',
    borderRadius: 1,
    transition: (theme) =>
      theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shorter,
      }),
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.common.white, 0.08),
    },
  };

  const menuSurfaceSx: SxProps<Theme> = {
    py: 0.5,
    minWidth: 180,
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: 4,
    overflow: 'hidden',
  };

  const headerProfileName = formatProfileNameForHeader(userData?.firstname, userData?.lastname);

  const hubTriggerSx: SxProps<Theme> = {
    cursor: 'pointer',
    borderRadius: 1,
    px: 1,
    py: 0.5,
    ml: -1,
    transition: (theme) =>
      theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shorter,
      }),
    '&:hover': {
      bgcolor: (theme) =>
        theme.palette.mode === 'light'
          ? alpha(theme.palette.text.primary, 0.12)
          : alpha(theme.palette.common.white, 0.18),
    },
  };

  return (
    <ArcStack 
      py={2} 
      pr={2}
      pl={11}
      height={HEADER_HEIGHT}
      justifyContent='center' 
      bgcolor='background.paper'>
      <ArcStack spacing={1} direction='row' justifyContent='space-between'>
        <ArcStack spacing={2} direction='row' alignItems='center'>
          <img src={BrandImage} alt='brand image' height={32} />
          <ArcStack
            direction='row'
            alignItems='center'
            sx={hubTriggerSx}
            role='button'
            aria-label='Open hubs'
            onClick={() => navigate(routesConfig.hubs)}>
            <ArcText.h4 fontSize={14} lineHeight={1.3}>
              {userData?.hub?.name}
            </ArcText.h4>
          </ArcStack>
        </ArcStack>

        <ArcStack direction='row' justifyContent='center' spacing={1} alignItems='center' sx={{ userSelect: 'none' }}>
          <Notifications />

          <ArcTooltip
            title={t('header.account')}
            open={accountTipOpen && !profileMenu.open}
            disableHoverListener
            disableFocusListener
            disableTouchListener>
            <span
              onMouseEnter={() => setAccountTipOpen(true)}
              onMouseLeave={() => setAccountTipOpen(false)}>
              <ArcStack
                direction='row'
                alignItems='center'
                spacing={0.75}
                sx={headerTriggerSx(profileMenu.open)}
                role='button'
                aria-haspopup='menu'
                aria-expanded={profileMenu.open}
                aria-label={t('header.account')}
                onClick={(event) => {
                  setAccountTipOpen(false);
                  profileMenu.toggle(event);
                }}>
                <ProfileCircleIcon width={20} height={20} aria-hidden />
                <ArcText.h4 fontSize={14} lineHeight={1.3}>
                  {headerProfileName}
                </ArcText.h4>
              </ArcStack>
            </span>
          </ArcTooltip>

          <Popper
            ref={menuRef}
            open={profileMenu.open}
            anchorEl={profileMenu.anchorEl}
            placement='bottom-end'
            sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}>
            <ArcStack sx={{ pt: '9px' }}>
              <ArcStack sx={menuSurfaceSx}>
                <ArcStack sx={menuRowSx} onClick={() => goTo(routesConfig.profile)}>
                  <ArcStack direction='row' alignItems='center' spacing={0.5} sx={{ color: 'common.white' }}>
                    <ProfileIconSvg width={18} height={18} aria-hidden />
                    <ArcText.h4 fontSize={14} fontWeight={500} color='common.white'>Profile</ArcText.h4>
                  </ArcStack>
                </ArcStack>
                <ArcStack sx={menuRowSx} onClick={() => goTo(routesConfig.hubs)}>
                  <ArcStack direction='row' alignItems='center' spacing={0.5} sx={{ color: 'common.white' }}>
                    <HubIconSvg width={18} height={18} aria-hidden />
                    <ArcText.h4 fontSize={14} fontWeight={500} color='common.white'>Hubs</ArcText.h4>
                  </ArcStack>
                </ArcStack>
                <ArcStack sx={menuRowSx} onClick={() => { profileMenu.close(); void logout(); }}>
                  <ArcStack direction='row' alignItems='center' spacing={0.5}>
                    <LogoutIcon sx={{ fontSize: 18, color: 'error.main' }} />
                    <ArcText.h4 fontSize={14} fontWeight={500} color='error.main'> 
                        Log Out
                    </ArcText.h4>
                  </ArcStack>
                </ArcStack>
              </ArcStack>
            </ArcStack>
          </Popper>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

export default Header;
